import React from 'react';
import { ClipLoader } from 'react-spinners';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { FaDownload } from 'react-icons/fa';
import '../Reports.css';

function ProcedimentosTab({
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    bookingsLoading,
    bookings,
    handleSearch,
    handleQuickFilter,
    groupBookingsByProcedures,
    setBookings,
    professionals
}) {
    const getProfessionalName = (professionalId) => {
        const professional = professionals.find(p => p.id === professionalId);
        return professional ? professional.name : professionalId;
    };

    const downloadSimplifiedCSV = () => {
        const realizadoBookings = bookings.filter(booking => booking.status === 'Realizado');
        
        if (realizadoBookings.length === 0) {
            alert('Nenhum agendamento realizado para exportar');
            return;
        }

        const groups = {
            'Consulta': { count: 0, totalValue: 0 },
            'Cirurgia': { count: 0, totalValue: 0 },
            'Procedimento Dermatológico': { count: 0, totalValue: 0 }
        };

        realizadoBookings.forEach(booking => {
            const procedures = booking.procedimentosList || booking.procedimentos || [];
            
            procedures.forEach(proc => {
                const name = (proc.nome || '').toUpperCase();
                const value = parseFloat(proc.valor || 0);

                if (name.includes('RETORNO')) {
                    return; // Ignorar Retorno de Consulta
                }

                if (name.includes('CONSULTA')) {
                    groups['Consulta'].count += 1;
                    groups['Consulta'].totalValue += value;
                } else if (name.includes('CIRURGIA')) {
                    groups['Cirurgia'].count += 1;
                    groups['Cirurgia'].totalValue += value;
                } else {
                    groups['Procedimento Dermatológico'].count += 1;
                    groups['Procedimento Dermatológico'].totalValue += value;
                }
            });
        });

        const headers = ['Categoria', 'Quantidade', 'Valor Total'];
        const escapeCSV = (value) => {
            if (value === null || value === undefined) return '';
            const str = String(value);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const rows = Object.entries(groups).map(([category, data]) => [
            escapeCSV(category),
            escapeCSV(data.count),
            escapeCSV(data.totalValue.toFixed(2))
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.join(','))
        ].join('\n');

        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `relatorio_simplificado_${startDate}_${endDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadCSV = () => {
        const realizadoBookings = bookings.filter(booking => booking.status === 'Realizado');
        
        if (realizadoBookings.length === 0) {
            alert('Nenhum agendamento realizado para exportar');
            return;
        }

        const rows = [];
        
        realizadoBookings.forEach(booking => {
            const procedures = booking.procedimentosList || booking.procedimentos || [];
            
            if (procedures.length === 0) {
                rows.push({
                    data: booking.datetimeAtendimento ? booking.datetimeAtendimento.split('T')[0] : '',
                    hora: booking.datetimeAtendimento ? booking.datetimeAtendimento.split('T')[1]?.replace('Z', '').substring(0, 5) : '',
                    paciente: booking.paciente || '',
                    profissional: getProfessionalName(booking.profissional),
                    procedimento: '',
                    valor: '',
                    comissao: '',
                    duracao: booking.duration || '',
                    status: booking.status || '',
                    observacao: booking.observacao || '',
                    canal: booking.booking_channel || '',
                    agente: booking.booking_agent || '',
                    valorTotal: booking.valorTotal || ''
                });
            } else {
                procedures.forEach(proc => {
                    rows.push({
                        data: booking.datetimeAtendimento ? booking.datetimeAtendimento.split('T')[0] : '',
                        hora: booking.datetimeAtendimento ? booking.datetimeAtendimento.split('T')[1]?.replace('Z', '').substring(0, 5) : '',
                        paciente: booking.paciente || '',
                        profissional: getProfessionalName(booking.profissional),
                        procedimento: proc.nome || '',
                        valor: proc.valor || '',
                        comissao: proc.commission || '',
                        duracao: booking.duration || '',
                        status: booking.status || '',
                        observacao: booking.observacao || '',
                        canal: booking.booking_channel || '',
                        agente: booking.booking_agent || '',
                        valorTotal: booking.valorTotal || ''
                    });
                });
            }
        });

        const headers = ['Data', 'Hora', 'Paciente', 'Profissional', 'Procedimento', 'Valor', 'Comissao', 'Duracao (min)', 'Status', 'Observacao', 'Canal', 'Agente', 'Valor Total'];
        
        const escapeCSV = (value) => {
            if (value === null || value === undefined) return '';
            const str = String(value);
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const csvContent = [
            headers.join(','),
            ...rows.map(row => [
                escapeCSV(row.data),
                escapeCSV(row.hora),
                escapeCSV(row.paciente),
                escapeCSV(row.profissional),
                escapeCSV(row.procedimento),
                escapeCSV(row.valor),
                escapeCSV(row.comissao),
                escapeCSV(row.duracao),
                escapeCSV(row.status),
                escapeCSV(row.observacao),
                escapeCSV(row.canal),
                escapeCSV(row.agente),
                escapeCSV(row.valorTotal)
            ].join(','))
        ].join('\n');

        const BOM = '\uFEFF';
        const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `relatorio_procedimentos_${startDate}_${endDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    // Prepare data for pie chart
    const preparePieChartData = () => {
        const proceduresData = groupBookingsByProcedures();
        const sortedProcedures = Object.entries(proceduresData)
            .sort(([,a], [,b]) => b.totalValue - a.totalValue);
        
        const totalRevenue = sortedProcedures.reduce((sum, [, data]) => sum + data.totalValue, 0);
        
        if (totalRevenue === 0) return [];
        
        // Get top 10 procedures
        const top10 = sortedProcedures.slice(0, 10);
        const others = sortedProcedures.slice(10);
        
        // Create pie chart data
        const pieData = top10.map(([name, data]) => ({
            name,
            value: data.totalValue,
            percentage: ((data.totalValue / totalRevenue) * 100).toFixed(1),
            count: data.count
        }));
        
        // Add "Outros" if there are procedures beyond top 10
        if (others.length > 0) {
            const othersValue = others.reduce((sum, [, data]) => sum + data.totalValue, 0);
            const othersCount = others.reduce((sum, [, data]) => sum + data.count, 0);
            pieData.push({
                name: 'Outros',
                value: othersValue,
                percentage: ((othersValue / totalRevenue) * 100).toFixed(1),
                count: othersCount
            });
        }
        
        return pieData;
    };

    // Colors for the pie chart
    const COLORS = [
        '#4a90e2', '#66bb6a', '#ffa726', '#ef5350', '#ab47bc',
        '#26c6da', '#ffee58', '#8d6e63', '#78909c', '#ff7043',
        '#bdbdbd' // For "Outros"
    ];

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div style={{
                    backgroundColor: '#2a2a2a',
                    border: '1px solid #333',
                    borderRadius: '4px',
                    padding: '10px',
                    color: '#e0e0e0'
                }}>
                    <p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
                    <p style={{ margin: '5px 0 0 0' }}>
                        Valor: R$ {data.value.toFixed(2)} ({data.percentage}%)
                    </p>
                    <p style={{ margin: '5px 0 0 0' }}>
                        Quantidade: {data.count}
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="caixa-container">
            <div className="caixa-search-filters">
                <div className="date-filters">
                    <div className="quick-filters">
                        <button 
                            className="quick-filter-button"
                            onClick={() => handleQuickFilter(7)}
                        >
                            7d
                        </button>
                        <button 
                            className="quick-filter-button"
                            onClick={() => handleQuickFilter(15)}
                        >
                            15d
                        </button>
                        <button 
                            className="quick-filter-button"
                            onClick={() => handleQuickFilter(30)}
                        >
                            30d
                        </button>
                    </div>
                    <div className="date-input-group">
                        <label>Início:</label>
                        <input 
                            type="date" 
                            value={startDate} 
                            onChange={(e) => {
                                setStartDate(e.target.value);
                                setBookings([]); // Clear current bookings
                            }}
                            className="date-input"
                            required
                            onClick={(e) => e.target.showPicker()}
                        />
                    </div>
                    <div className="date-input-group">
                        <label>Fim:</label>
                        <input 
                            type="date" 
                            value={endDate} 
                            onChange={(e) => {
                                setEndDate(e.target.value);
                                setBookings([]); // Clear current bookings
                            }}
                            className="date-input"
                            required
                            onClick={(e) => e.target.showPicker()}
                        />
                    </div>
                    <button 
                        className="dark-button"
                        onClick={handleSearch}
                        disabled={bookingsLoading}
                    >
                        Filtrar
                    </button>
                    <button 
                        className="dark-button"
                        onClick={downloadCSV}
                        disabled={bookingsLoading || bookings.filter(b => b.status === 'Realizado').length === 0}
                        title="Baixar CSV com todos os agendamentos"
                    >
                        <FaDownload style={{ marginRight: '5px' }} />
                        Baixar CSV
                    </button>
                    <button 
                        className="dark-button"
                        onClick={downloadSimplifiedCSV}
                        disabled={bookingsLoading || bookings.filter(b => b.status === 'Realizado').length === 0}
                        title="Baixar CSV simplificado agrupado por categoria"
                        style={{ marginLeft: '10px' }}
                    >
                        <FaDownload style={{ marginRight: '5px' }} />
                        CSV Simplificado
                    </button>
                </div>
            </div>

            {bookingsLoading ? (
                <div className="loading-container">
                    <ClipLoader size={50} color={"#4a90e2"} />
                </div>
            ) : (
                <div className="bookings-list">
                    {/* Pie Chart Section */}
                    {Object.keys(groupBookingsByProcedures()).length > 0 && (
                        <div className="procedure-group">
                            <h3>Distribuição de Receita por Procedimento</h3>
                            <div>
                                <ResponsiveContainer width="100%" height={400}>
                                    <PieChart>
                                        <Pie
                                            data={preparePieChartData()}
                                            cx="50%"
                                            cy="50%"
                                            outerRadius={120}
                                            fill="#8884d8"
                                            dataKey="value"
                                            label={({ percentage }) => `${percentage}%`}
                                            labelLine={false}
                                        >
                                            {preparePieChartData().map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<CustomTooltip />} />
                                        <Legend 
                                            wrapperStyle={{ color: '#e0e0e0' }}
                                            formatter={(value) => value.length > 25 ? `${value.substring(0, 25)}...` : value}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}

                    {/* Table Section */}
                    <div className="procedure-group">
                        <h3>Relatório de Procedimentos - Últimos {Math.round((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24))} dias</h3>
                        <table>
                            <thead>
                                <tr>
                                    <th>Procedimento</th>
                                    <th>Quantidade</th>
                                    <th>Valor Total</th>
                                    <th>Valor Médio</th>
                                    <th>% do Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {Object.entries(groupBookingsByProcedures())
                                    .sort(([,a], [,b]) => b.totalValue - a.totalValue) // Sort by total value desc
                                    .map(([procedureName, data]) => {
                                        const totalRevenue = Object.values(groupBookingsByProcedures())
                                            .reduce((sum, proc) => sum + proc.totalValue, 0);
                                        const percentage = totalRevenue > 0 ? ((data.totalValue / totalRevenue) * 100).toFixed(1) : '0.0';
                                        
                                        return (
                                            <tr key={procedureName}>
                                                <td><strong>{procedureName}</strong></td>
                                                <td>{data.count}</td>
                                                <td>R$ {data.totalValue.toFixed(2)}</td>
                                                <td>R$ {(data.totalValue / data.count).toFixed(2)}</td>
                                                <td>{percentage}%</td>
                                            </tr>
                                        );
                                    })}
                                {Object.keys(groupBookingsByProcedures()).length === 0 && !bookingsLoading && (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                            Nenhum procedimento encontrado no período selecionado
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {Object.keys(groupBookingsByProcedures()).length > 0 && (
                        <div className="grand-total">
                            <h3>Resumo Geral</h3>
                            <div className="total-value">
                                <div>
                                    Total de Procedimentos: {Object.values(groupBookingsByProcedures())
                                        .reduce((total, data) => total + data.count, 0)}
                                </div>
                                <div>
                                    Valor Total: R$ {Object.values(groupBookingsByProcedures())
                                        .reduce((total, data) => total + data.totalValue, 0)
                                        .toFixed(2)}
                                </div>
                            </div>
                            <div className="procedure-stats">
                                <div>
                                    Tipos de Procedimentos: {Object.keys(groupBookingsByProcedures()).length}
                                </div>
                                <div>
                                    Valor Médio por Procedimento: R$ {Object.keys(groupBookingsByProcedures()).length > 0 
                                        ? (Object.values(groupBookingsByProcedures())
                                            .reduce((total, data) => total + data.totalValue, 0) / 
                                           Object.values(groupBookingsByProcedures())
                                            .reduce((total, data) => total + data.count, 0))
                                            .toFixed(2)
                                        : '0.00'}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default ProcedimentosTab; 